import type { PersonaListItem } from '@/hooks/usePersonas'

/**
 * Client-side search over the full Personas list (MASTER.md Q.2: low
 * volume, "2–5× organizaciones" — no server-side search endpoint exists,
 * same pattern as Relaciones/Personas list filtering). Matches title,
 * email or cargo, case-insensitive substring. Requires at least 2
 * characters to avoid a noisy match-everything result on the first keystroke.
 */
export function findPersonaMatches(personas: PersonaListItem[], query: string, limit = 6): PersonaListItem[] {
  const needle = query.trim().toLowerCase()
  if (needle.length < 2) return []

  return personas
    .filter((p) => [p.title, p.email, p.cargo].filter((v): v is string => Boolean(v)).some((v) => v.toLowerCase().includes(needle)))
    .slice(0, limit)
}

/**
 * Finds an existing persona whose email exactly matches (case-insensitive).
 *
 * Model note (MASTER.md Q.2): `Personas.Organizacion` is a required,
 * single-value lookup — a persona cannot belong to two organizaciones in
 * the same row. The accepted V1 behavior for the same human at a second
 * organización is a second row (not a shared row), so email is enforced
 * as globally unique (it's also the index used for Graph participant
 * matching, which would be ambiguous across two rows sharing an email).
 * This function is the client-side mirror of that same check the backend
 * enforces authoritatively in `POST /api/personas` (409 on collision).
 */
export function findEmailConflict(personas: PersonaListItem[], email: string | null | undefined): PersonaListItem | null {
  const needle = (email ?? '').trim().toLowerCase()
  if (!needle) return null
  return personas.find((p) => (p.email ?? '').trim().toLowerCase() === needle) ?? null
}
